import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import api from '../services/api';
import gridBg from '../assets/grid.svg';

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalTasks: 0,
    completedTasks: 0,
    newUsersLastWeek: 0
  });
  const [users, setUsers] = useState([]);
  const [activityPeriod, setActivityPeriod] = useState('daily');
  const [activityData, setActivityData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, [activityPeriod]); // Add activityPeriod dependency

  const fetchDashboardData = async () => {
    try {
      console.log('Fetching dashboard data...');
      
      // Stats verisi için doğru endpoint kullan  
      const statsResponse = await api.get('/dashboard/stats');
      console.log('Stats response:', statsResponse.data);
      
      // Users verisi
      const usersResponse = await api.get('/users');
      console.log('Users response:', usersResponse.data);
      
      // Verileri hemen tanımla
      const statsData = statsResponse.data || {};
      const usersData = usersResponse.data || [];
      
      // Activity verisi - period parametresi ile
      let activityData = [];
      try {
        const activityResponse = await api.get(`/dashboard/activity?period=${activityPeriod}`);
        activityData = activityResponse.data || [];
        console.log('Activity response:', activityData);
      } catch (activityError) {
        console.warn('Activity endpoint not available:', activityError);
        activityData = [];
      }
      
      setStats({
        totalUsers: statsData.totalUsers || 0,
        totalTasks: statsData.totalTasks || 0,
        completedTasks: statsData.completedTasks || 0,
        newUsersLastWeek: statsData.newUsersLastWeek || 0
      });
      
      // Son 5 kullanıcıyı al
      const recentUsers = Array.isArray(usersData) ? usersData.slice(0, 5) : [];
      setUsers(recentUsers);
      setActivityData(activityData);
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // Hata durumunda sıfır değerler
      setStats({
        totalUsers: 0,
        totalTasks: 0,
        completedTasks: 0,
        newUsersLastWeek: 0
      });
      setUsers([]);
      setActivityData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    navigate('/login');
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  const recentUsers = Array.isArray(users) ? users.slice(0, 5) : [];

  return (
    <div className="relative z-10">
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#FF3366] to-[#FF6B4D]">
          Dashboard Overview
        </h2>
      </div>

      {/* Stats Cards - with gradients */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <StatCard 
          title="Total Users" 
          value={stats.totalUsers} 
          className="bg-gradient-to-br from-[#151515] to-[#0A0A0A] border border-[#FF3366]/10" 
          titleColor="text-gray-300"
        />
        <StatCard 
          title="Total Tasks" 
          value={stats.totalTasks} 
          className="bg-gradient-to-br from-[#151515] to-[#0A0A0A] border border-[#FF3366]/10" 
          titleColor="text-gray-300"
        />
        <StatCard 
          title="Completed Tasks" 
          value={stats.completedTasks} 
          className="bg-gradient-to-br from-[#151515] to-[#0A0A0A] border border-[#FF3366]/10" 
          titleColor="text-gray-300"
        />
        <StatCard 
          title="New Users (Last Week)" 
          value={stats.newUsersLastWeek} 
          className="bg-gradient-to-br from-[#151515] to-[#0A0A0A] border border-[#FF3366]/10" 
          titleColor="text-gray-300"
        />
      </div>

      {/* Charts and Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Activity Chart */}
        <div className="bg-[#151515] rounded-xl overflow-hidden mb-8 border border-[#FF3366]/10 p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-semibold text-white">User Registration & Task Activity</h3>
            <div className="flex space-x-2 items-center">
              {['daily', 'weekly', 'monthly'].map((period) => (
                <button
                  key={period}
                  onClick={() => setActivityPeriod(period)}
                  className={`px-3 py-1 text-xs rounded-lg capitalize ${
                    activityPeriod === period
                      ? 'bg-gradient-to-r from-[#FF3366] to-[#FF6B4D] text-white'
                      : 'bg-[#0A0A0A] text-gray-400 hover:text-[#FF3366]'
                  }`}
                >
                  {period}
                </button>
              ))}
              <div className="w-px h-4 bg-gray-600 mx-2"></div>
              <button
                onClick={fetchDashboardData}
                disabled={loading}
                className="flex items-center gap-1 px-3 py-1 text-xs rounded-lg bg-[#0A0A0A] text-gray-400 hover:text-[#FF3366] hover:bg-[#FF3366]/10 transition-all"
                title="Refresh data"
              >
                <svg 
                  className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth="2" 
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" 
                  />
                </svg>
                Refresh
              </button>
            </div>
          </div>
          
          {activityData.length > 0 ? (
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={activityData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
                  <XAxis 
                    dataKey="name" 
                    stroke="#FF3366" 
                    tick={{ fill: '#FF3366', fontSize: 12 }} 
                  />
                  <YAxis 
                    stroke="#FF3366" 
                    tick={{ fill: '#FF3366', fontSize: 12 }}
                    domain={[0, (dataMax) => {
                      if (dataMax <= 10) return 10;
                      if (dataMax <= 50) return 50;
                      if (dataMax <= 100) return 100;
                      return Math.ceil(dataMax / 100) * 100;
                    }]}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#0A0A0A',
                      border: '1px solid #FF3366',
                      borderRadius: '0.5rem'
                    }}
                  />
                  <Bar dataKey="logins" fill="#FF3366" name="User Registrations" />
                  <Bar dataKey="completions" fill="#FF6B4D" name="Task Completions" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-80 flex items-center justify-center">
              <div className="text-center">
                <div className="text-6xl mb-4">📊</div>
                <p className="text-gray-400">No activity data available</p>
                <p className="text-gray-500 text-sm mt-2">Activity data will appear here once users start logging in</p>
              </div>
            </div>
          )}
        </div>

        {/* Recent Users Table */}
        <div className="bg-[#151515] rounded-xl p-6 shadow-lg transition-all duration-300 border border-[#FF3366]/10">
          <h3 className="text-lg font-semibold mb-6 text-white">
            Recent Users
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-[#151515] rounded-xl">
              <thead>
                <tr className="border-b border-[#FF3366]/10">
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#FF3366] uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#FF3366] uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#FF3366] uppercase tracking-wider">
                    Joined
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#FF3366]/10">
                {recentUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-800/50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {user.avatar_url && (
                          <img 
                            src={user.avatar_url} 
                            alt="" 
                            className="h-8 w-8 rounded-full mr-3"
                          />
                        )}
                        <span className="text-gray-300">{user.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-300">
                      {user.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-300">
                      {new Date(user.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ title, value, className, titleColor }) => (
  <div className={`${className} rounded-xl border border-gray-800 p-6 shadow-lg hover:shadow-xl transition-all duration-300`}>
    <div className="flex flex-col">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 rounded-lg bg-black/20">
          {title === "Total Users" && (
            <div className="text-[#FF3366]">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
              </svg>
            </div>
          )}
          {title === "Total Tasks" && (
            <div className="text-[#FF6B4D]">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
              </svg>
            </div>
          )}
          {title === "Completed Tasks" && (
            <div className="text-[#FF3366]">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </div>
          )}
          {title === "New Users (Last Week)" && (
            <div className="text-[#FF6B4D]">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
              </svg>
            </div>
          )}
        </div>
        <h3 className={`text-sm font-medium ${titleColor}`}>{title}</h3>
      </div>
      <div className="flex items-baseline">
        <p className="text-3xl font-bold text-white">
          {typeof value === 'number' ? value.toLocaleString() : value}
        </p>
      </div>
    </div>
  </div>
);

export default Dashboard; 